import React, { useState, useEffect } from "react";
import { Container, Row, Col, Form } from "react-bootstrap";
import "../componentCss/webform.css";
import { Formik } from "formik";
import "../componentCss/sidebar.css";
import toast, { Toaster } from 'react-hot-toast';
import { Link, useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";

import config from "../../config/config";
import { Button } from "react-bootstrap";
import { loginSchema, otpLoginSchema } from "../../validations/loginSchema";
import {
  updateFcmTokenAction,
} from "../../Action/admin.action";
import { useDispatch, useSelector } from "react-redux";
import {
  login,
  initiateOTPLogin,
  verifyOTP,
  resendOTP,
  selectLoading,
  selectError,
  selectIsAuthenticated,
  selectLoggedInUser,
  selectLoginAttempts,
  resetLoginAttempts,
  selectOTPSession,
  selectOTPLoading,
  selectOTPError,
  selectIsOTPLoginInProgress,
  selectCanResendOTP,
  clearOTPSession,
  setAuth
} from "../../redux/slices/userSlices";
import {
  verifyLogin,
  selectMFARequired,
  selectMFAVerified,
  selectMFALoading,
  selectVerificationAttempts,
  selectRemainingAttempts,
  selectCanRetryVerification,
  setMFARequired,
  setMFAVerified,
  resetVerificationState
} from "../../redux/slices/mfaSlice";
import { getFirebaseToken } from "../../firebase";
import authService from "../../services/auth.service";
import mfaService from "../../services/mfa.service";
import { DEBUG_MODE } from "../../config/apiConfig";
import { getFieldErrors } from "../../lib/utils/normalizeError";

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Redux state selectors
  const loading = useSelector(selectLoading);
  const error = useSelector(selectError);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const loggedInUser = useSelector(selectLoggedInUser);
  const loginAttempts = useSelector(selectLoginAttempts);

  // OTP state selectors
  const otpSession = useSelector(selectOTPSession);
  const otpLoading = useSelector(selectOTPLoading);
  const otpError = useSelector(selectOTPError);
  const isOTPLoginInProgress = useSelector(selectIsOTPLoginInProgress);
  const canResendOTP = useSelector(selectCanResendOTP);

  // MFA state selectors
  const mfaRequired = useSelector(selectMFARequired);
  const mfaVerified = useSelector(selectMFAVerified);
  const mfaLoading = useSelector(selectMFALoading);
  const verificationAttempts = useSelector(selectVerificationAttempts);
  const remainingAttempts = useSelector(selectRemainingAttempts);
  const canRetryVerification = useSelector(selectCanRetryVerification);

  // Local component state
  const [formData, setForm] = useState({
    email: "",
    password: "",
    userGroupId: 0,
    otpCode: "",
    mfaCode: "",
    mfaSessionId: "" // Store MFA session ID from initiate-login
  });
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const [resendTimer, setResendTimer] = useState(0);
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [loginMethod, setLoginMethod] = useState('otp'); // 'otp' or 'mfa'

  // Check for existing authentication on component mount
  useEffect(() => {
    const checkExistingAuth = async () => {
      try {
        // Check if user is already authenticated
        if (authService.isAuthenticated()) {
          const userInfo = authService.getUserInfo();

          if (userInfo) {

            // Update FCM token if available
            if (userInfo.id) {
              updateFcmToken(userInfo.id);
            }

            // Redirect based on role
            redirectBasedOnRole(userInfo.role);
            return;
          }
        }

        // Validate existing token if present
        const token = authService.getAccessToken();
        if (token) {
          try {
            const isValid = await authService.validateToken();
            if (!isValid) {
              // Token is invalid, clear session
              authService.clearSession();
            }
          } catch (error) {
            authService.clearSession();
          }
        }
      } catch (error) {
        authService.clearSession();
      }
    };

    checkExistingAuth();
  }, [navigate]);

  // Handle authentication state changes
  useEffect(() => {
    if (isAuthenticated && loggedInUser) {

      // Update FCM token
      if (loggedInUser.data?.user_info?.id || loggedInUser.id) {
        const userId = loggedInUser.data?.user_info?.id || loggedInUser.id;
        updateFcmToken(userId);
      }

      // Small delay to ensure localStorage is properly set
      setTimeout(() => {
        const role = loggedInUser.data?.user_info?.role || loggedInUser.role;
        redirectBasedOnRole(role);
      }, 100);
    }
  }, [isAuthenticated, loggedInUser, navigate]);

  // OTP Timer management
  useEffect(() => {
    let interval;

    if (otpSession.expiresIn > 0) {
      setOtpTimer(otpSession.expiresIn);
      interval = setInterval(() => {
        setOtpTimer(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [otpSession.expiresIn]);

  // Resend Timer management  
  useEffect(() => {
    let interval;

    if (otpSession.canResendIn > 0) {
      setResendTimer(otpSession.canResendIn);
      interval = setInterval(() => {
        setResendTimer(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [otpSession.canResendIn]);

  // Clean up OTP session on component unmount
  useEffect(() => {
    return () => {
      if (otpSession.sessionId && !isAuthenticated) {
        dispatch(clearOTPSession());
      }
    };
  }, []);

  // Redirect user based on their role
  const redirectBasedOnRole = (role) => {
    try {
      // Check for nurse roles - redirect directly to patient records
      const nurseRoles = ['nurse', 'nurse_manager', '3', '4'];

      if (nurseRoles.includes(role)) {
        navigate(`${config.baseUrl}patient-records`);
        return;
      }

      // Check for admin roles
      const adminRoles = ['super_admin', 'admin', '1', '2'];

      if (adminRoles.includes(role)) {
        navigate(`${config.baseUrl}dashboard`);
      } else {
        navigate(`${config.baseUrl}patient-records`);
      }
    } catch (error) {
      toast.error("Redirect failed. Please try logging in again.");
    }
  };

  // Handle form submission - OTP-based login with MFA support
  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    try {
      setIsSubmitting(true);
      setSubmitting(true);

      // Check for too many login attempts
      if (loginAttempts >= 5) {
        toast.error("Too many failed login attempts. Please wait before trying again.", {
          position: "top-right",
          duration: 5000,
        });
        setSubmitting(false);
        setIsSubmitting(false);
        return;
      }

      // If MFA verification is required
      if (mfaRequired && values.mfaCode) {
        if (!values.mfaSessionId) {
          toast.error("MFA session not found. Please restart login.", {
            position: "top-right",
            duration: 4000,
          });
          return;
        }

        const result = await dispatch(verifyLogin({
          sessionId: values.mfaSessionId,
          mfaCode: values.mfaCode
        })).unwrap();

        if (result.success) {
          dispatch(setMFAVerified(true));
          dispatch(resetLoginAttempts());

          // Update authentication state in userSlice (critical fix!)
          if (result.data && result.data.user_info) {
            dispatch(setAuth({
              user: result.data.user_info,
              token: result.data.access_token,
              refreshToken: result.data.refresh_token
            }));
          }

          toast.success("Login successful", {
            position: "top-right",
            duration: 3000,
          });

          // Clear form
          setForm(prev => ({ ...prev, mfaCode: "" }));
        } else {
          toast.error(result.message || "Invalid MFA code", {
            position: "top-right",
            duration: 4000,
          });
        }
      }
      // If OTP login is in progress, verify OTP
      else if (isOTPLoginInProgress && values.otpCode) {
        const result = await dispatch(verifyOTP({
          sessionId: otpSession.sessionId,
          otpCode: values.otpCode
        })).unwrap();

        if (result.success) {
          // Check if user has MFA enabled
          const mfaStatus = await mfaService.getMFAStatus();

          if (mfaStatus.success && mfaStatus.data.mfaEnabled) {
            // User has MFA enabled - require MFA verification
            dispatch(setMFARequired(true));
            toast.success("OTP verified. Please enter your authenticator code.", {
              position: "top-right",
              duration: 3000,
            });
            // Preserve email when transitioning to MFA phase
            setForm(prev => ({
              ...prev,
              email: values.email, // Preserve email
              otpCode: ""
            }));
          } else {
            // No MFA - complete login as normal
            dispatch(resetLoginAttempts());
            toast.success("Login successful", {
              position: "top-right",
              duration: 3000,
            });
            setForm(prev => ({ ...prev, otpCode: "" }));
          }
        } else {
          // Show OTP verification error
          toast.error(result.message || "Invalid OTP code", {
            position: "top-right",
            duration: 4000,
          });
        }
      } else {
        // Initial credential validation
        if (!values.email || !values.password) {
          toast.error("Please enter both email and password", {
            position: "top-right",
            duration: 3000,
          });
          setSubmitting(false);
          setIsSubmitting(false);
          return;
        }

        // Handle different login methods
        if (loginMethod === 'otp') {
          // Email OTP login
          const result = await dispatch(initiateOTPLogin({
            username: values.email.trim(),
            password: values.password
          })).unwrap();

          if (result.success) {
            // OTP sent successfully
            toast.success(result.message || "OTP sent to your email", {
              position: "top-right",
              duration: 3000,
            });

            // Clear password field for security
            setForm(prev => ({ ...prev, password: "" }));
          } else {
            // Show credential validation error
            const apiErrorMessage = result.message || result.error || "Login failed";
            toast.error(apiErrorMessage, {
              position: "top-right",
              duration: 4000,
            });
          }
        } else {
          // MFA login (authenticator app)
          // Initiate MFA login which validates credentials and checks MFA status
          const mfaInitResult = await mfaService.initiateMFALogin(
            values.email.trim(),
            values.password
          );

          if (mfaInitResult.success) {
            // Credentials verified and MFA is enabled
            // Store session ID for MFA verification and preserve email (clear password for security)
            setForm(prev => ({
              ...prev,
              email: values.email, // Preserve email to prevent Formik reinitialization from clearing it
              password: values.password,
              mfaCode: "", // Clear password for security
              mfaSessionId: mfaInitResult.data.sessionId
            }));
            dispatch(setMFARequired(true));
            toast.success("Credentials verified. Please enter your authenticator code.", {
              position: "top-right",
              duration: 3000,
            });
          } else {
            // Either credentials invalid or MFA not enabled
            toast.error(mfaInitResult.message || "MFA login failed", {
              position: "top-right",
              duration: 5000,
            });
          }
        }
      }
    } catch (error) {
      console.log("Login error:", error);

      // Show exact API error message from backend response
      // Check error.message first to handle Redux thunk rejections and service errors
      const apiErrorMessage = error.message ||
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Login failed. Please try again.";

      toast.error(apiErrorMessage, {
        position: "top-right",
        duration: 4000,
      });

      // Handle field-specific errors from validation
      const fieldErrors = getFieldErrors(error);
      Object.keys(fieldErrors).forEach(field => {
        setFieldError(field, fieldErrors[field].join(', '));
      });
    } finally {
      setSubmitting(false);
      setIsSubmitting(false);
    }
  };

  // Handle OTP resend
  const handleResendOTP = async () => {
    try {
      if (!otpSession.sessionId) {
        toast.error("No active OTP session found", {
          position: "top-right",
          duration: 3000,
        });
        return;
      }

      const result = await dispatch(resendOTP({
        sessionId: otpSession.sessionId
      })).unwrap();

      if (result.success) {
        // Success message is handled by Redux (toast is shown in the async thunk)
      } else {
        toast.error(result.message || "Failed to resend OTP", {
          position: "top-right",
          duration: 4000,
        });
      }
    } catch (error) {
      toast.error("Failed to resend OTP. Please try again.", {
        position: "top-right",
        duration: 4000,
      });
    }
  };

  // Handle back to login (cancel OTP/MFA)
  const handleBackToLogin = () => {
    dispatch(clearOTPSession());
    dispatch(resetVerificationState());
    setForm(prev => ({
      ...prev,
      password: "",
      otpCode: "",
      mfaCode: ""
    }));
    setUseBackupCode(false);
  };

  // Toggle between backup code and TOTP
  const handleToggleBackupCode = () => {
    setUseBackupCode(!useBackupCode);
    setForm(prev => ({ ...prev, mfaCode: "" }));
  };

  // Toggle between OTP and MFA login methods
  const handleToggleLoginMethod = () => {
    setLoginMethod(loginMethod === 'otp' ? 'mfa' : 'otp');
  };

  // Update FCM token for push notifications
  const updateFcmToken = async (userId) => {
    try {
      const firebaseToken = await getFirebaseToken();
      if (firebaseToken && userId) {
        const tokenData = {
          id: userId,
          fcm_token: firebaseToken
        };

        await updateFcmTokenAction(tokenData);
      }
    } catch (error) {
      // Handle Firebase permission errors gracefully
      if (error.code === 'messaging/permission-blocked' ||
        error.code === 'messaging/permission-denied' ||
        error.message?.includes('permission')) {
        // Don't show error to user as this is not critical for login
        return;
      }

      // Don't show error to user as this is not critical for login
    }
  };

  // Handle password visibility toggle
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Handle remember me functionality
  const handleRememberMe = (checked) => {
    setRememberMe(checked);
    // You can implement remember me functionality here
    // For example, store a flag in localStorage
    if (checked) {
      localStorage.setItem('rememberMe', 'true');
    } else {
      localStorage.removeItem('rememberMe');
    }
  };

  // Check if form should be disabled
  const isFormDisabled = loading || otpLoading || isSubmitting || loginAttempts >= 5;

  // Helper function to format time (mm:ss)
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <>
      <div className="wrapper">
        <div id="content">
          <Container fluid className="p-4">
            <Row className="loginForm">
              <Col lg={12} md={12} sm={12} className="">
                <div className="formDiv">
                  <Formik
                    initialValues={formData}
                    validationSchema={isOTPLoginInProgress ? otpLoginSchema : loginSchema}
                    onSubmit={handleSubmit}
                    enableReinitialize
                    context={{ showOTP: isOTPLoginInProgress }}
                  >
                    {({
                      errors,
                      handleSubmit,
                      handleBlur,
                      handleChange,
                      touched,
                      values,
                      isSubmitting: formikSubmitting,
                      setFieldError
                    }) => (
                      <Form onSubmit={handleSubmit}>
                        {/* Logo Section */}
                        <Row className="col-md-12">
                          <div className="col-md-4"></div>
                          <div className="col-md-4 text-center">
                            <img
                              src={`${config.baseUrl}assets/images/logo2.png`}
                              alt="Neurula Health Logo"
                              style={{ maxWidth: "200px", height: "auto" }}
                            />
                          </div>
                        </Row>


                        {/* Login Attempts Warning */}
                        {loginAttempts >= 3 && loginAttempts < 5 && (
                          <Row className="col-md-12 mt-3">
                            <div className="col-md-4"></div>
                            <div className="col-md-4">
                              <div className="alert alert-warning" role="alert">
                                Warning: {5 - loginAttempts} login attempts remaining
                              </div>
                            </div>
                          </Row>
                        )}

                        {/* Account Locked Warning */}
                        {loginAttempts >= 5 && (
                          <Row className="col-md-12 mt-3">
                            <div className="col-md-4"></div>
                            <div className="col-md-4">
                              <div className="alert alert-danger" role="alert">
                                Account temporarily locked due to too many failed attempts.
                                Please wait and try again later.
                              </div>
                            </div>
                          </Row>
                        )}

                        {/* Email/Username Field */}
                        <Row className="col-md-12 mt-4">
                          <div className="col-md-4"></div>
                          <div className="col-md-4">
                            <Form.Label>Email or Username *</Form.Label>
                            <input
                              className={`form-control ${errors.email && touched.email ? 'is-invalid' : ''}`}
                              name="email"
                              type="text"
                              onChange={handleChange}
                              placeholder="Please enter email or username"
                              onBlur={handleBlur}
                              value={values.email}
                              disabled={isFormDisabled}
                              autoComplete="username"
                            />
                            {errors.email && touched.email && (
                              <div className="invalid-feedback">{errors.email}</div>
                            )}
                          </div>
                        </Row>

                        {/* Password Field */}
                        <Row className="col-md-12 mt-3">
                          <div className="col-md-4"></div>
                          <div className="col-md-4">
                            <Form.Label>Password *</Form.Label>
                            <div className="input-group">
                              <input
                                className={`form-control ${errors.password && touched.password ? 'is-invalid' : ''}`}
                                type={showPassword ? "text" : "password"}
                                name="password"
                                onChange={handleChange}
                                placeholder="Please enter password"
                                onBlur={handleBlur}
                                value={values.password}
                                disabled={isFormDisabled}
                                autoComplete="current-password"
                              />
                              <button
                                className="btn btn-outline-secondary"
                                type="button"
                                onClick={togglePasswordVisibility}
                                disabled={isFormDisabled}
                                style={{ borderLeft: 'none' }}
                              >
                                {showPassword ? '👁️' : '👁️‍🗨️'}
                              </button>
                            </div>
                            {errors.password && touched.password && (
                              <div className="text-danger mt-1">{errors.password}</div>
                            )}
                          </div>
                        </Row>

                        {/* Login Method Selection - Only show before any verification starts */}
                        {!isOTPLoginInProgress && !mfaRequired && (
                          <Row className="col-md-12 mt-4">
                            <div className="col-md-4"></div>
                            <div className="col-md-4">
                              <div style={{
                                padding: '12px',
                                background: '#f8f9fa',
                                borderRadius: '8px',
                                marginBottom: '8px'
                              }}>
                                <div style={{
                                  display: 'block',
                                  marginBottom: '12px',
                                  fontSize: '14px',
                                  color: '#666',
                                  textAlign: 'center'
                                }}>
                                  Choose your verification method:
                                </div>
                                <div className="d-flex gap-2" style={{ gap: '12px' }}>
                                  <button
                                    type="button"
                                    className={`btn w-50 ${loginMethod === 'otp' ? 'btn-primary' : 'btn-outline-primary'}`}
                                    onClick={() => setLoginMethod('otp')}
                                    disabled={isFormDisabled}
                                    style={{
                                      padding: '10px',
                                      fontSize: '14px',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'center',
                                      gap: '4px'
                                    }}
                                  >
                                    <span style={{ fontSize: '20px' }}>📧</span>
                                    <span>Email OTP</span>
                                  </button>
                                  <button
                                    type="button"
                                    className={`btn w-50 ${loginMethod === 'mfa' ? 'btn-primary' : 'btn-outline-primary'}`}
                                    onClick={() => setLoginMethod('mfa')}
                                    disabled={isFormDisabled}
                                    style={{
                                      padding: '10px',
                                      fontSize: '14px',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'center',
                                      gap: '4px'
                                    }}
                                  >
                                    <span style={{ fontSize: '20px' }}>🔐</span>
                                    <span>Authenticator</span>
                                  </button>
                                </div>
                              </div>
                            </div>
                          </Row>
                        )}

                        {/* OTP Verification Section */}
                        {isOTPLoginInProgress && (
                          <>
                            {/* OTP Instructions */}
                            <Row className="col-md-12 mt-4">
                              <div className="col-md-4"></div>
                              <div className="col-md-4">
                                <div className="alert alert-info" role="alert">
                                  <div className="d-flex align-items-start">
                                    <div className="me-2">📧</div>
                                    <div>
                                      <strong>OTP Sent!</strong>
                                      <p className="mb-1 mt-1">
                                        We've sent a 6-digit verification code to{' '}
                                        <strong>{otpSession.emailMasked}</strong>
                                      </p>
                                      <p className="mb-0">
                                        <small>
                                          {otpTimer > 0 ? (
                                            <>Code expires in: <strong>{formatTime(otpTimer)}</strong></>
                                          ) : (
                                            <span className="text-warning">⚠️ Code has expired</span>
                                          )}
                                        </small>
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </Row>

                            {/* OTP Input Field */}
                            <Row className="col-md-12 mt-3">
                              <div className="col-md-4"></div>
                              <div className="col-md-4">
                                <Form.Label>Verification Code *</Form.Label>
                                <input
                                  className={`form-control text-center ${errors.otpCode && touched.otpCode ? 'is-invalid' : ''}`}
                                  name="otpCode"
                                  type="text"
                                  onChange={handleChange}
                                  placeholder="Enter 6-digit code"
                                  onBlur={handleBlur}
                                  value={values.otpCode}
                                  disabled={isFormDisabled}
                                  maxLength="6"
                                  autoComplete="one-time-code"
                                  style={{
                                    fontSize: '18px',
                                    letterSpacing: '3px',
                                    fontFamily: 'monospace'
                                  }}
                                />
                                {errors.otpCode && touched.otpCode && (
                                  <div className="invalid-feedback">{errors.otpCode}</div>
                                )}
                                {otpSession.attemptsRemaining !== null && otpSession.attemptsRemaining < 3 && (
                                  <small className="text-warning">
                                    ⚠️ {otpSession.attemptsRemaining} attempts remaining
                                  </small>
                                )}
                              </div>
                            </Row>

                            {/* OTP Actions */}
                            <Row className="col-md-12 mt-3">
                              <div className="col-md-4"></div>
                              <div className="col-md-4 d-flex justify-content-between align-items-center">
                                <button
                                  type="button"
                                  className="btn btn-link text-primary p-0"
                                  onClick={handleBackToLogin}
                                  disabled={isFormDisabled}
                                >
                                  ← Back to Login
                                </button>
                                {/* <button
                                  type="button"
                                  className="btn btn-link text-primary p-0"
                                  onClick={handleResendOTP}
                                  disabled={isFormDisabled || resendTimer > 0 || !canResendOTP}
                                >
                                  {resendTimer > 0 ? (
                                    `Resend in ${formatTime(resendTimer)}`
                                  ) : (
                                    'Resend OTP'
                                  )}
                                </button> */}
                              </div>
                            </Row>
                          </>
                        )}

                        {/* MFA Verification Section */}
                        {mfaRequired && !mfaVerified && (
                          <>
                            {/* MFA Instructions */}
                            <Row className="col-md-12 mt-4">
                              <div className="col-md-4"></div>
                              <div className="col-md-4">
                                <div className="alert alert-info" role="alert">
                                  <div className="d-flex align-items-start">
                                    <div className="me-2">🔒</div>
                                    <div>
                                      <strong>Two-Factor Authentication Required</strong>
                                      <p className="mb-1 mt-1">
                                        {useBackupCode
                                          ? 'Enter one of your backup codes to continue'
                                          : 'Open your authenticator app and enter the 6-digit code for Neurula Health'
                                        }
                                      </p>
                                      {!useBackupCode && (
                                        <p className="mb-0">
                                          <small>
                                            ⏱️ Code refreshes every 30 seconds
                                          </small>
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </Row>

                            {/* MFA Input Field */}
                            <Row className="col-md-12 mt-3">
                              <div className="col-md-4"></div>
                              <div className="col-md-4">
                                <Form.Label>{useBackupCode ? 'Backup Code *' : 'Authenticator Code *'}</Form.Label>
                                <input
                                  className={`form-control text-center ${errors.mfaCode && touched.mfaCode ? 'is-invalid' : ''}`}
                                  name="mfaCode"
                                  type="text"
                                  onChange={handleChange}
                                  placeholder={useBackupCode ? "A3F9-K2L8-M4N7-P6Q5" : "000000"}
                                  onBlur={handleBlur}
                                  value={values.mfaCode || ''}
                                  disabled={!canRetryVerification}
                                  maxLength={useBackupCode ? "19" : "6"}
                                  autoComplete="one-time-code"
                                  style={{
                                    fontSize: '18px',
                                    letterSpacing: useBackupCode ? '2px' : '3px',
                                    fontFamily: 'monospace'
                                  }}
                                />
                                {errors.mfaCode && touched.mfaCode && (
                                  <div className="invalid-feedback">{errors.mfaCode}</div>
                                )}
                                {verificationAttempts > 0 && canRetryVerification && (
                                  <small className="text-warning">
                                    ⚠️ {remainingAttempts} attempts remaining
                                  </small>
                                )}
                                {!canRetryVerification && (
                                  <small className="text-danger d-block mt-2">
                                    ⚠️ Too many failed attempts. Please wait 15 minutes before trying again.
                                  </small>
                                )}
                              </div>
                            </Row>

                            {/* MFA Actions */}
                            <Row className="col-md-12 mt-3">
                              <div className="col-md-4"></div>
                              <div className="col-md-4 d-flex justify-content-between align-items-center">
                                <button
                                  type="button"
                                  className="btn btn-link text-primary p-0"
                                  onClick={handleBackToLogin}
                                  disabled={mfaLoading}
                                >
                                  ← Back to Login
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-link text-primary p-0"
                                  onClick={handleToggleBackupCode}
                                  disabled={mfaLoading || !canRetryVerification}
                                >
                                  {useBackupCode ? '🔐 Use Authenticator' : '🔑 Use Backup Code'}
                                </button>
                              </div>
                            </Row>
                          </>
                        )}

                        {/* Remember Me and Forgot Password */}
                        <Row className="col-md-12 mt-3">
                          <div className="col-md-4"></div>
                          <div className="col-md-4 d-flex justify-content-between align-items-center">
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id="rememberMe"
                                checked={rememberMe}
                                onChange={(e) => handleRememberMe(e.target.checked)}
                                disabled={isFormDisabled}
                              />
                              <label className="form-check-label" htmlFor="rememberMe">
                                Remember me
                              </label>
                            </div>
                            {/* <Link
                              to={`${config.baseUrl}forgot-password`}
                              className="text-primary text-decoration-none"
                            >
                              Forgot Password?
                            </Link> */}
                          </div>
                        </Row>

                        {/* Submit Button */}
                        <Row className="col-md-12 mt-4">
                          <div className="col-md-4"></div>
                          <div className="col-md-4">
                            <Button
                              type="submit"
                              className="btn btn-primary w-100"
                              disabled={isFormDisabled || formikSubmitting || mfaLoading || (mfaRequired && !canRetryVerification)}
                              style={{
                                backgroundColor: '#007bff',
                                borderColor: '#007bff',
                                padding: '12px',
                                fontSize: '16px',
                                fontWeight: '500'
                              }}
                            >
                              {loading || otpLoading || isSubmitting || formikSubmitting || mfaLoading ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                  {mfaRequired ? 'Verifying MFA...' : isOTPLoginInProgress ? 'Verifying...' : 'Signing In...'}
                                </>
                              ) : (
                                mfaRequired ? (useBackupCode ? 'Verify Backup Code' : 'Verify MFA') : isOTPLoginInProgress ? 'Verify OTP' : 'Sign In'
                              )}
                            </Button>
                          </div>
                        </Row>

                        {/* Additional Links */}
                        <Row className="col-md-12 mt-4">
                          <div className="col-md-4"></div>
                          <div className="col-md-4 text-center">
                            <p className="mb-0">
                              <small className="text-muted">
                                Need help? Contact{' '}
                                <a href="mailto:support@neurula.health" className="text-primary">
                                  support@neurula.health
                                </a>
                              </small>
                            </p>
                          </div>
                        </Row>


                      </Form>
                    )}
                  </Formik>
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      </div>

      {/* Toast Container */}
      <Toaster />
    </>
  );
};

export default Login;
